var _ = require('underscore');
var TorsoNestedModel = require('backbone-torso/modules/NestedModel');
var appointmentTypeExtService = require('./../urls').appointmentTypeExtService;

/**
 * Model for appointment types
 *
 * @class AppointmentTypeModel
 * @extends Torso.NestedModel
 * @constructor
 */
module.exports = (TorsoNestedModel.extend({
  idAttribute: 'externalId',
  urlRoot: appointmentTypeExtService,

  /**
   * Initializes AppointmentTypeModel
   * Sets nested property preRegConfig to an empty object if it doesn't exist
   *
   * @method initialize
   * @override
   */
  initialize: function() {
    if (_.isUndefined(this.get('preRegConfig'))) {
      this.set('preRegConfig', {});
    }
  },

  /**
   * Override isNew method so that new appointment types with a defined external id can be created
   * @method isNew
   * @override
   */
  isNew: function() {
    return _.isUndefined(this.get('effectiveStartDate'));
  },

  /**
   * @method setParent
   * @param parentAppointmentTypeModel {AppointmentTypeModel} the parent appointment type model for this appointment type.
   */
  setParent: function(parentAppointmentTypeModel) {
    var oldAppointmentTypeExternalId = this._parentAppointmentTypeModel && this._parentAppointmentTypeModel.get('externalId');
    var newAppointmentTypeExternalId = parentAppointmentTypeModel && parentAppointmentTypeModel.get('externalId');
    this._parentAppointmentTypeModel = parentAppointmentTypeModel;
    if (oldAppointmentTypeExternalId !== newAppointmentTypeExternalId) {
      this.trigger('change change:parent', parentAppointmentTypeModel);
    }
  },

  /**
   * @method getParent
   * @return {AppointmentTypeModel} the parent appointment type model for this appointment type.
   */
  getParent: function() {
    return this._parentAppointmentTypeModel;
  },

  /**
   * @method getEffectivePreRegEnabled
   * @returns {boolean} whether or not pre reg is enabled
   */
  getEffectivePreRegEnabled: function() {
    if (this.get('preRegEnabled') === undefined && this.getParent()) {
      return this.getParent().getEffectivePreRegEnabled();
    }
    return this.get('preRegEnabled');
  },

  /**
   * @method getEffectiveCheckInEnabled
   * @returns {boolean} whether or not check in is enabled
   */
  getEffectiveCheckInEnabled: function() {
    if (this.get('checkInEnabled') === undefined && this.getParent()) {
      return this.getParent().getEffectiveCheckInEnabled();
    }
    return this.get('checkInEnabled');
  },

  /**
   * @method getEffectiveAllowEstimatedPay
   * @returns {boolean} whether or not to allow estimated pay
   */
  getEffectiveAllowEstimatedPay: function() {
    if (this.get('allowEstimatedPay') === undefined && this.getParent()) {
      return this.getParent().getEffectiveAllowEstimatedPay();
    }
    return this.get('allowEstimatedPay');
  },

  /**
   * @method getEffectiveAllowOutstandingPay
   * @returns {boolean} whether or not to allow outstanding pay
   */
  getEffectiveAllowOutstandingPay: function() {
    if (this.get('allowOutstandingPay') === undefined && this.getParent()) {
      return this.getParent().getEffectiveAllowOutstandingPay();
    }
    return this.get('allowOutstandingPay');
  },

  /**
   * @method getEffectiveInstructions
   * @returns {String} the instructions for the appointment type
   */
  getEffectiveInstructions: function() {
    if (this.get('instructions') === undefined && this.getParent()) {
      return this.getParent().getEffectiveInstructions();
    }
    return this.get('instructions');
  },

  /**
   * @method getEffectiveAppointmentTimeReplacement
   * @returns {String} the appointment time replacement to display as text for the appointment type
   */
  getEffectiveAppointmentTimeReplacement: function() {
    if (this.get('appointmentTimeReplacement') === undefined && this.getParent()) {
      return this.getParent().getEffectiveAppointmentTimeReplacement();
    }
    return this.get('appointmentTimeReplacement');
  },

  /**
   * @method getEffectiveForms
   * @returns {Object} map of document names to review periods in days
   */
  getEffectiveForms: function() {
    if (this.get('formsInherit') && this.getParent()) {
      return _.extend(this.getParent().getEffectiveForms(), this.get('forms'));
    }
    return this.get('forms');
  },

  /**
   * @method getEffectiveSurveys
   * @returns {Object} map of survey names to review periods in days
   */
  getEffectiveSurveys: function() {
    if (this.get('surveysInherit') && this.getParent()) {
      return _.extend(this.getParent().getEffectiveSurveys(), this.get('surveys'));
    }
    return this.get('surveys');
  },

  /**
   * @method getEffectiveFirstDayToPreReg
   * @returns {Number} the number of days before appointment that pre-registration can be started
   */
  getEffectiveFirstDayToPreReg: function() {
    if (this.get('preRegConfig').firstDayToPreReg === undefined && this.getParent()) {
      return this.getParent().getEffectiveFirstDayToPreReg();
    }
    return this.get('preRegConfig').firstDayToPreReg;
  },

  /**
   * @method getEffectiveLastDayToPreReg
   * @returns {Number} the number of days before appointment that pre-registration closes
   */
  getEffectiveLastDayToPreReg: function() {
    if (this.get('preRegConfig').lastDayToPreReg === undefined && this.getParent()) {
      return this.getParent().getEffectiveLastDayToPreReg();
    }
    return this.get('preRegConfig').lastDayToPreReg;
  },

  /**
   * @method getEffectiveReminders
   * @returns {Array} Array of minutes before an appointment to send reminders
   */
  getEffectiveReminders: function() {
    var shouldInherit = this.get('preRegConfig').remindersInherit && !_.isUndefined(this.getParent());
    var inheritedReminders;
    if (shouldInherit) {
      inheritedReminders = this.getParent().getEffectiveReminders();
    }
    var customReminders = this.get('preRegConfig').reminders;
    return _.union(inheritedReminders, customReminders);
  },

  /**
   * @method getEffectiveCorrespondenceSettings
   * @returns {Object} map of email restrictions by email type
   */
  getEffectiveCorrespondenceSettings: function() {
    if (this.get('correspondenceSettingsInherit') && this.getParent()) {
      return this.getParent().getEffectiveCorrespondenceSettings();
    }
    return this.get('correspondenceSettings');
  },

  /**
   * @method getEffectiveEnabledWizardSteps
   * @returns {Array} array of enabled wizard steps
   */
  getEffectiveEnabledWizardSteps: function() {
    if (this.get('preRegConfig').enabledWizardStepsInherit && this.getParent()) {
      return this.getParent().getEffectiveEnabledWizardSteps();
    }
    return this.get('preRegConfig').enabledWizardSteps;
  }
}));
